import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { Accordion } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { BankIcon, CreditCardIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { formatCashFull } from "utils/formatCash";
import { PayLinkQuote } from "hooks/usePayLinkSession";

const METHOD_LABELS: Record<string, string> = {
  card: "Card",
  us_bank_account: "Bank account",
  ach_debit: "Bank account",
  bank_transfer: "Bank transfer",
  klarna: "Klarna",
  affirm: "Affirm",
  ideal: "iDEAL",
};

const METHOD_ICONS: Record<string, typeof CreditCardIcon> = {
  card: CreditCardIcon,
  us_bank_account: BankIcon,
  ach_debit: BankIcon,
  bank_transfer: BankIcon,
};

interface PayLinkPaymentMethodPickerProps {
  methods: string[];
  quotes: Record<string, PayLinkQuote> | null;
  currency: string;
  selected: string;
  disabled?: boolean;
  chosen?: boolean;
  renderExpanded?: (paymentMethodType: string) => React.ReactNode;
  onSelect: (paymentMethodType: string) => void;
}

/* Choose and price the method before opening its single-method session. */
const PayLinkPaymentMethodPicker: React.FC<PayLinkPaymentMethodPickerProps> = ({
  methods,
  quotes,
  currency,
  selected,
  disabled = false,
  chosen = false,
  renderExpanded,
  onSelect,
}) => {
  const { t } = useTranslation();
  // The chosen method's checkout can fold away and back without giving the method up.
  const [folded, setFolded] = useState<string | null>(null);

  return (
    <Stack sx={{ gap: "var(--space-3)" }}>
      <Typography variant="subtitle2">
        {chosen || methods.length < 2
          ? t("Payment method")
          : t("How would you like to pay")}
      </Typography>

      <Box>
        {methods.map((method) => {
          const quote = quotes?.[method];
          const discount = quote?.discountAmount || 0;
          const isSelected = selected === method;
          const icon = METHOD_ICONS[method];

          return (
            <Accordion
              key={method}
              title={t(METHOD_LABELS[method] || method)}
              icon={icon ? <HugeiconsIcon icon={icon} size={20} /> : undefined}
              description={
                quote ? (
                  <Inline gap="var(--space-2)">
                    <span>{formatCashFull(quote.totalToCharge, currency)}</span>
                    {discount > 0 && (
                      <Typography
                        variant="caption"
                        component="span"
                        color="success"
                      >
                        {t("payLink.PayByBankSave", {
                          amount: formatCashFull(discount, currency),
                        })}
                      </Typography>
                    )}
                  </Inline>
                ) : undefined
              }
              expanded={isSelected && folded !== method}
              disabled={disabled && !isSelected}
              onChange={(_event, open) => {
                if (isSelected) {
                  setFolded(open ? null : method);
                } else if (open) {
                  setFolded(null);
                  onSelect(method);
                }
              }}
            >
              {isSelected ? renderExpanded?.(method) : null}
            </Accordion>
          );
        })}
      </Box>
    </Stack>
  );
};

export default PayLinkPaymentMethodPicker;
